import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiX, HiSparkles, HiMail } from "react-icons/hi";
import { FaGoogle } from "react-icons/fa";

import useOutsideClick from "../../hooks/useOutsideClick";
import {
  selectGeneral,
  setShowAuthModal,
  setShowRegisterModal,
} from "../../redux/slices/generalSlice";

/**
 * AuthModal Component (Simplified)
 * 
 * Authentication modal supporting:
 * - Email/Password login & registration
 * - Google OAuth for sign-up and login
 * 
 * Wallet connection is a separate feature in profile settings,
 * not an authentication method.
 */
const AuthModal = () => {
  const dispatch = useDispatch();
  const { showAuthModal, authModalRedirect } = useSelector(selectGeneral);
  const modalRef = useRef(null);
  const [mode, setMode] = useState("login"); // login, register, forgot
  const [isLoading, setIsLoading] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");

  const handleClose = () => {
    dispatch(setShowAuthModal({ show: false }));
    setMode("login");
    setFormData({ email: "", password: "", confirmPassword: "" });
    setError("");
  };

  useOutsideClick(modalRef, handleClose, null, showAuthModal);

  // Handle Google OAuth login
  const handleGoogleLogin = () => {
    setIsLoading("google");
    const redirectParam = authModalRedirect ? `?redirect=${encodeURIComponent(authModalRedirect)}` : "";
    window.location.href = `${process.env.REACT_APP_BASE_URL}/auth/google${redirectParam}`;
  };

  // Handle email/password submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading("email");

    try {
      if (mode === "register") {
        if (formData.password !== formData.confirmPassword) {
          setError("Passwords do not match");
          setIsLoading(null);
          return;
        }
        
        // Validate password requirements before proceeding
        const password = formData.password;
        if (password.length < 8) {
          setError("Password must be at least 8 characters");
          setIsLoading(null);
          return;
        }
        if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
          setError("Password must contain uppercase, lowercase, and a number");
          setIsLoading(null);
          return;
        }
        
        // Check if email already exists
        const checkResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/check-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email }),
        });
        const checkData = await checkResponse.json();
        
        if (checkData.exists) {
          setError("An account with this email already exists. Please login instead.");
          setIsLoading(null);
          return;
        }
        
        // Store credentials temporarily in sessionStorage (secure, cleared on browser close)
        sessionStorage.setItem("pending_signup", JSON.stringify({
          email: formData.email,
          password: formData.password,
          auth_provider: "email"
        }));
        
        // Close modal and redirect to CreateProfile
        dispatch(setShowAuthModal({ show: false }));
        window.location.href = "/create-profile?signup=email";
        return;
      }

      if (mode === "forgot") {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email }),
        });
        const data = await response.json();
        setError(data.message);
        setIsLoading(null);
        return;
      }

      // Login
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message);
        setIsLoading(null);
        return;
      }

      // Store token and redirect
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("auth_provider", data.user.auth_provider);
      
      window.location.href = data.user.profile_step < 1 
        ? "/create-profile" 
        : (authModalRedirect || "/");
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setIsLoading(null);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  // Body overflow control
  useEffect(() => {
    if (showAuthModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showAuthModal]);

  if (!showAuthModal) return null;

  return (
    <div className="min-w-[100vw] w-full h-screen flex justify-center items-center bg-secondary/80 backdrop-blur-md fixed top-0 left-0 z-50 p-4">
      <div
        ref={modalRef}
        className="w-full max-w-md flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transform transition-all animate-fadeIn"
      >
        {/* Header */}
        <div className="relative w-full p-8 pb-4 text-center">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200"
          >
            <HiX className="text-gray-600" size={18} />
          </button>

          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primary-green/10 items-center justify-center mb-4">
            <HiSparkles className="text-primary" size={28} />
          </div>
          <h2 className="text-2xl font-bold text-secondary mb-2">
            {mode === "login" && "Welcome Back"}
            {mode === "register" && "Create Account"}
            {mode === "forgot" && "Reset Password"}
          </h2>
          <p className="text-gray-500 text-sm">
            {mode === "login" && "Sign in to continue to FunDeSci"}
            {mode === "register" && "Join the decentralized science community"}
            {mode === "forgot" && "Enter your email to receive a reset link"}
          </p>
        </div>

        {/* Google Login */}
        {mode !== "forgot" && (
          <div className="w-full px-8 py-4">
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:bg-red-50 hover:border-red-200 ${
                isLoading === "google" ? "opacity-70 cursor-wait" : ""
              }`}
            >
              <FaGoogle className="text-red-500" size={20} />
              <span className="font-medium text-gray-700">
                Continue with Google
              </span>
            </button>
          </div>
        )}

        {/* Divider */}
        {mode !== "forgot" && (
          <div className="w-full px-8 py-2">
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="w-full px-8 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
              placeholder="your@email.com"
            />
          </div>

          {mode !== "forgot" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          )}

          {mode === "register" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          )}

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300 ${
              isLoading === "email" ? "opacity-70 cursor-wait" : ""
            }`}
          >
            <HiMail size={20} />
            <span>
              {mode === "login" && "Sign In"}
              {mode === "register" && "Create Account"}
              {mode === "forgot" && "Send Reset Link"}
            </span>
          </button>
        </form>

        {/* Mode Toggles */}
        <div className="w-full px-8 pb-6 text-center text-sm">
          {mode === "login" && (
            <>
              <button
                onClick={() => setMode("forgot")}
                className="text-primary hover:underline"
              >
                Forgot password?
              </button>
              <p className="text-gray-500 mt-2">
                Don't have an account?{" "}
                <button
                  onClick={() => setMode("register")}
                  className="text-primary hover:underline font-medium"
                >
                  Sign up
                </button>
              </p>
            </>
          )}
          {mode === "register" && (
            <p className="text-gray-500">
              Already have an account?{" "}
              <button
                onClick={() => setMode("login")}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          )}
          {mode === "forgot" && (
            <button
              onClick={() => setMode("login")}
              className="text-primary hover:underline"
            >
              Back to sign in
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="w-full px-8 py-4 bg-gray-50 border-t border-gray-100">
          <p className="text-center text-xs text-gray-500">
            By continuing, you agree to our{" "}
            <a href="/terms" className="text-primary hover:underline">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/terms" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
